"use client";

import { ArrowRight, CheckCircle2, Loader as Loader2 } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
const ORGANISATION_OPTIONS = ["Golf Course", "Golf Resort", "Multi-Course Group", "Destination or Tourism Organisation", "Hotel or Accommodation Partner", "Transport or Ground Handler", "Other Golf-Travel Partner"];
const REASON_OPTIONS = ["Claim or correct our course profile", "Discuss an annual partnership", "Promote a specific priority or campaign", "Explore destination collaboration", "Discuss future package inclusion", "General partnership enquiry"];
const fieldClassName = ["h-[46px]", "rounded-[9px]", "border-[#DED3BF]", "bg-[#FFFDF7]", "px-4", "text-[14px]", "text-[#073F31]", "shadow-none", "placeholder:text-[#65746D]/65", "focus-visible:border-[#B79A5F]", "focus-visible:ring-2", "focus-visible:ring-[#B79A5F]/18"].join(" ");
const labelClassName = ["mb-1.5 block", "text-[11px]", "font-semibold", "tracking-[0.02em]", "text-[#29483E]"].join(" ");
function PartnershipForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex min-h-[400px] flex-col items-center justify-center px-5 py-12 text-center">
        <div className={["flex size-[58px]", "items-center justify-center", "rounded-full", "border border-[#D8C69B]", "bg-[#F7F3E9]", "text-[#073F31]"].join(" ")}>
          <CheckCircle2 className="size-7" strokeWidth={1.6} />
        </div>

        <h3 className="mt-5 text-[30px] font-medium text-[#073F31]" style={{
        fontFamily: '"Playfair Display", Georgia, "Times New Roman", serif'
      }}>
          Enquiry received.
        </h3>

        <p className="mt-3 max-w-[470px] text-[14px] leading-[1.7] text-[#65746D]">
          {successMessage}
        </p>

        <Button type="button" variant="outline" onClick={resetForm} className={["mt-7 h-[48px]", "rounded-[9px]", "border-[#B79A5F]/60", "bg-transparent", "px-6", "text-[11px] font-bold uppercase", "tracking-[0.07em]", "text-[#073F31]", "hover:border-[#073F31]", "hover:bg-[#F7F3E9]"].join(" ")}>
          Send Another Enquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 gap-x-5 gap-y-5 md:grid-cols-2">
        <FormField control={control} name="organisation_type" rules={{
        required: "Please select an organisation type"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClassName}>
                Organisation Type
              </FormLabel>

              <Select value={field.value || ""} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger className={fieldClassName}>
                    <SelectValue placeholder="Select organisation type" />
                  </SelectTrigger>
                </FormControl>

                <SelectContent>
                  {ORGANISATION_OPTIONS.map(option => <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>)}
                </SelectContent>
              </Select>

              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="reason_for_contacting" rules={{
        required: "Please select a reason"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClassName}>
                Reason for Contacting
              </FormLabel>

              <Select value={field.value || ""} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger className={fieldClassName}>
                    <SelectValue placeholder="Select enquiry type" />
                  </SelectTrigger>
                </FormControl>

                <SelectContent>
                  {REASON_OPTIONS.map(option => <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>)}
                </SelectContent>
              </Select>

              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="course_name" rules={{
        required: "Course or organisation name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClassName}>
                Course / Organisation Name
              </FormLabel>

              <FormControl>
                <Input placeholder="e.g. Green Valley Golf Club" className={fieldClassName} {...field} />
              </FormControl>

              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="business_email" rules={{
        required: "Business email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClassName}>
                Business Email
              </FormLabel>

              <FormControl>
                <Input type="email" placeholder="name@company.com" className={fieldClassName} {...field} />
              </FormControl>

              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="contact_name" rules={{
        required: "Contact name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClassName}>
                Contact Name
              </FormLabel>

              <FormControl>
                <Input placeholder="Your full name" className={fieldClassName} {...field} />
              </FormControl>

              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone_whatsapp" rules={{
        required: "Phone or WhatsApp number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClassName}>
                Phone / WhatsApp
              </FormLabel>

              <FormControl>
                <Input type="tel" placeholder="+852 1234 5678" className={fieldClassName} {...field} />
              </FormControl>

              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="position" rules={{
        required: "Position or title is required"
      }} render={({
        field
      }) => <FormItem className="md:col-span-2">
              <FormLabel className={labelClassName}>
                Position / Title
              </FormLabel>

              <FormControl>
                <Input placeholder="e.g. General Manager or Director of Sales" className={fieldClassName} {...field} />
              </FormControl>

              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message_priorities" rules={{
        required: "Please tell us about your priorities"
      }} render={({
        field
      }) => <FormItem className="md:col-span-2">
              <FormLabel className={labelClassName}>
                Message / Business Priorities
              </FormLabel>

              <FormControl>
                <Textarea placeholder="Tell us what matters most to your course or organisation..." className={["min-h-[112px]", "resize-y", "rounded-[9px]", "border-[#DED3BF]", "bg-[#FFFDF7]", "px-4 py-3", "text-[14px]", "leading-[1.65]", "text-[#073F31]", "shadow-none", "placeholder:text-[#65746D]/65", "focus-visible:border-[#B79A5F]", "focus-visible:ring-2", "focus-visible:ring-[#B79A5F]/18"].join(" ")} {...field} />
              </FormControl>

              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="rounded-[9px] border border-red-300/70 bg-red-50 px-4 py-3 text-[13px] text-red-700">
          {submitError}
        </div>}

      <div className="pt-1">
        <Button type="submit" disabled={isSubmitting} className={["group h-[52px]", "w-full rounded-[9px]", "bg-[#BFFF00]", "px-7", "text-[11px] font-bold uppercase", "tracking-[0.075em]", "text-[#073F31]", "shadow-[0_9px_24px_rgba(7,63,49,0.14)]", "transition-all duration-300", "hover:-translate-y-[2px]", "hover:bg-[#CBFF36]", "hover:shadow-[0_13px_28px_rgba(7,63,49,0.18)]", "md:w-auto"].join(" ")}>
          {isSubmitting ? <>
              <Loader2 className="mr-2 size-4 animate-spin" />
              Sending Enquiry
            </> : <>
              Send Partnership Enquiry

              <ArrowRight className="ml-2 size-4 transition-transform duration-200 group-hover:translate-x-1" strokeWidth={1.7} />
            </>}
        </Button>

        <p className="mt-3 text-[11px] leading-[1.5] text-[#65746D]">
          Your details will only be used to respond to this partnership enquiry.
        </p>
      </div>
    </Form>;
}
export default function PartnerEnquiry() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="partner-enquiry" aria-labelledby="partner-enquiry-heading" className="relative w-full overflow-hidden bg-[#F7F3E9] !h-[800px] ![padding:60px_32px_80px_32px]" style={{
    fontFamily: '"Raleway", Arial, sans-serif'
  }}>
      <div aria-hidden="true" className={["pointer-events-none absolute inset-0", "bg-[radial-gradient(circle_at_16%_46%,rgba(183,154,95,0.10),transparent_27%),radial-gradient(circle_at_82%_18%,rgba(191,255,0,0.05),transparent_23%)]"].join(" ")} />

      <div ref={ref} className={["relative z-10 mx-auto grid w-full", "max-w-[1480px]", "grid-cols-1 items-center", "gap-10", "transition-all duration-700 ease-out", "lg:grid-cols-[minmax(340px,0.78fr)_minmax(0,1.22fr)]", "lg:gap-16", "xl:gap-20", isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"].join(" ")}>
        <div className="max-w-[540px] !p-0 !m-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9A793A]"><span className="!text-xs">Partnership Enquiry</span></p>

          <h2 id="partner-enquiry-heading" className="mt-4 font-medium leading-[1.04] tracking-[-0.035em] text-[#073F31] !text-6xl" style={{
          fontFamily: '"Playfair Display", Georgia, "Times New Roman", serif'
        }}>
            Let&apos;s explore the right partnership for your course.
          </h2>

          <p className="mt-6 max-w-[500px] leading-[1.72] text-[#596B63] !text-base"><span className="!text-lg" contenteditable="false" style={{}}><span className="!text-sm"><span className="!text-base">Tell us what matters most to your course or organisation. We will review the opportunity and contact you directly to discuss the most suitable next step</span>.</span></span></p>

          <div className="mt-7 border-l-2 border-[#B79A5F] pl-5">
            <p className="max-w-[430px] text-[14px] font-medium leading-[1.65] text-accent-foreground"><span className="text-accent" contenteditable="false" style={{}}><span className="text-primary"><span className="!text-[rgba(184,161,118,1)]"><span className="text-primary">A partnership discussion does not require a fixed package or public pricing. We begin with your priorities.</span></span></span></span></p>
          </div>
        </div>

        <div>
          <div className={["relative overflow-hidden", "rounded-[16px]", "border border-[#D8C69B]", "bg-[#FFFDF7]/94", "p-6", "shadow-[0_18px_42px_rgba(7,63,49,0.10)]", "backdrop-blur-[8px]", "md:p-8", "lg:p-9"].join(" ")}>
            <div aria-hidden="true" className="absolute inset-x-0 top-0 h-[3px] bg-[linear-gradient(90deg,#B79A5F,#D8C69B,#BFFF00)]" />

            <FormProvider formId="partnership_enquiry" sectionName="partner-enquiry" defaultValues={{
            organisation_type: "",
            course_name: "",
            contact_name: "",
            position: "",
            business_email: "",
            phone_whatsapp: "",
            reason_for_contacting: "",
            message_priorities: ""
          }} validationRules={{
            organisation_type: {
              required: true
            },
            course_name: {
              required: true
            },
            contact_name: {
              required: true
            },
            position: {
              required: true
            },
            business_email: {
              required: true,
              pattern: "email"
            },
            phone_whatsapp: {
              required: true
            },
            reason_for_contacting: {
              required: true
            },
            message_priorities: {
              required: true
            }
          }} submitText="Send Partnership Enquiry" successMessage="Thank you for your interest. We have received your enquiry and will contact you shortly.">
              <PartnershipForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}